import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { IoArrowBack, IoListOutline, IoClose, IoHomeOutline, IoCloudUploadOutline, IoDocumentAttachOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { getLesson, getGate, getFormation, completeLesson, listQuestions, addQuestion, uploadCarnetFile, openCarnetFile, recordResume } from "@/lib/api";

function useIsMobile(bp = 860) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth <= bp : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth <= bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
}

export default function Lecon() {
  const { lid } = useParams();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const { loading, isAuthenticated } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [gate, setGate] = useState(null);
  const [formation, setFormation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [submission, setSubmission] = useState("");
  const [qcmAnswers, setQcmAnswers] = useState([]);
  const [exFile, setExFile] = useState(null);         // {file_id, filename}
  const [result, setResult] = useState(null);         // retour QCM immédiat
  const [somOpen, setSomOpen] = useState(false);
  const [qText, setQText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: `/ecole/lecon/${lid}` } } });
  }, [loading, isAuthenticated, nav, lid]);

  const load = () => {
    getLesson(lid).then((l) => {
      setLesson(l);
      setSubmission(l.my_submission || "");
      const full = l.my_submission_full || {};
      setQcmAnswers(full.qcm_answers || []);
      setExFile(full.file_id ? { file_id: full.file_id, filename: full.filename } : null);
      if (l.gate?.id) getGate(l.gate.id).then(setGate).catch(() => {});
      if (l.gate?.formation_id) getFormation(l.gate.formation_id).then(setFormation).catch(() => {});
    }).catch(() => toast("Leçon introuvable"));
    listQuestions(lid).then(setQuestions).catch(() => {});
  };
  useEffect(() => { setResult(null); load(); recordResume(lid).catch(() => {}); }, [lid]); // eslint-disable-line react-hooks/exhaustive-deps

  // liste ordonnée des leçons de la porte pour "Suivant"
  const orderedLessons = useMemo(() => {
    if (!gate?.modules) return [];
    return gate.modules.flatMap((m) => m.lessons || []);
  }, [gate]);
  const nextLesson = useMemo(() => {
    const idx = orderedLessons.findIndex((l) => l.id === lid);
    return idx >= 0 && idx < orderedLessons.length - 1 ? orderedLessons[idx + 1] : null;
  }, [orderedLessons, lid]);

  const validate = async () => {
    setBusy(true);
    try {
      const etype = lesson.exercise_type || "paragraph";
      const payload = { scroll_ratio: 1 };
      if (etype === "qcm") payload.qcm_answers = qcmAnswers;
      else if (etype === "file") { payload.file_id = exFile?.file_id; payload.filename = exFile?.filename; }
      else payload.submission = submission;
      const res = await completeLesson(lid, payload);
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(20);
      if (res.submission?.kind === "qcm") setResult(res.submission);
      if (res.new_badge) toast.success(`🏅 Badge obtenu : ${res.new_badge.badge_label} !`, { duration: 4000 });
      else toast.success("Étape validée ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de valider.");
    } finally {
      setBusy(false);
    }
  };

  const toggleQcm = (i) => {
    setQcmAnswers((cur) => {
      if (lesson.qcm_multi) return cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i];
      return [i];
    });
  };
  const onExFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await uploadCarnetFile(f); setExFile({ file_id: r.file_id, filename: r.filename }); toast.success("Fichier prêt"); }
    catch (e) { toast.error(e?.response?.status === 413 ? "Fichier trop lourd (max 10 Mo)" : "Upload impossible"); }
    finally { setBusy(false); ev.target.value = ""; }
  };

  const postQuestion = async () => {
    if (!qText.trim()) return;
    try {
      await addQuestion(lid, { text: qText.trim() });
      setQText("");
      listQuestions(lid).then(setQuestions);
      toast.success("Question envoyée");
    } catch { toast.error("Connecte-toi pour poser une question."); }
  };

  if (!lesson) return <DojoShell crumbs={[{ label: "École", to: "/ecole" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement de la leçon…</p></DojoShell>;

  const crumbs = [
    { label: "École", to: "/ecole" },
    ...(formation ? [{ label: formation.title, to: `/ecole/formation/${formation.id}` }] : []),
    ...(lesson.gate ? [{ label: lesson.gate.title, to: `/ecole/porte/${lesson.gate.id}` }] : []),
    { label: lesson.title },
  ];
  const ytSrc = lesson.youtube_id
    ? `https://www.youtube-nocookie.com/embed/${lesson.youtube_id}?rel=0&modestbranding=1`
    : null;

  return (
    <DojoShell crumbs={crumbs}>
      <div style={{ ...c.grid, ...(isMobile ? { gridTemplateColumns: "1fr" } : {}) }}>
        <div style={c.left}>
          <div style={c.topBar}>
            <button style={c.backBtn} data-testid="lecon-back" onClick={() => lesson.gate ? nav(`/ecole/porte/${lesson.gate.id}`) : nav("/ecole")}><IoArrowBack size={17} /> Retour</button>
            <button style={c.backBtn} data-testid="lecon-back-portes" onClick={() => formation ? nav(`/ecole/formation/${formation.id}`) : nav("/ecole")}><IoHomeOutline size={16} /> Les Portes</button>
            <button style={c.somBtn} data-testid="lecon-sommaire-open" onClick={() => setSomOpen(true)}><IoListOutline size={17} /> Sommaire</button>
          </div>

          <h1 style={{ ...c.h1, ...(isMobile ? { fontSize: 22 } : {}) }}>{lesson.title}</h1>

          <div style={c.player}>
            {ytSrc ? (
              <iframe title={lesson.title} src={ytSrc} style={c.iframe} frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen />
            ) : (
              <div style={c.locked}>
                <span style={c.lockKanji}>🔒</span>
                <p style={c.lockTxt}>Cette vidéo est réservée aux abonnés.</p>
                <p style={c.lockSub}>{"L'abonnement arrive bientôt — reviens vite !"}</p>
              </div>
            )}
          </div>

          {lesson.rich_content && (
            <div style={c.card}>
              <div style={c.cardTitle}>Description</div>
              <div style={c.rich} dangerouslySetInnerHTML={{ __html: lesson.rich_content }} />
            </div>
          )}

          {(lesson.module?.resources || []).length > 0 && (
            <div style={c.card} data-testid="lecon-resources">
              <div style={c.cardTitle}>📚 Ressources pour t'aider</div>
              <div style={c.resGrid}>
                {lesson.module.resources.map((r) => (
                  <button key={r.id} style={c.resCard} data-testid={`lecon-resource-${r.id}`} onClick={() => nav(`/bibliotheque/${r.id}`)}>
                    <span style={c.resType}>{r.type || "Fiche"}</span>
                    <span style={c.resTitle}>{r.title}</span>
                    <span style={c.resGo}>Ouvrir dans la Bibliothèque →</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {(lesson.exercise || (lesson.exercise_type && lesson.exercise_type !== "paragraph")) && (
            <div style={c.card}>
              <div style={c.cardTitle}>🎯 Ton exercice</div>
              {lesson.exercise && <div style={c.rich} dangerouslySetInnerHTML={{ __html: lesson.exercise }} />}

              {lesson.exercise_type === "qcm" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }} data-testid="lecon-qcm">
                  {(lesson.qcm_options || []).map((o, i) => {
                    const sel = qcmAnswers.includes(i);
                    return (
                      <button key={i} onClick={() => toggleQcm(i)} data-testid={`qcm-opt-${i}`}
                        style={{ display: "flex", alignItems: "center", gap: 12, textAlign: "left", padding: "13px 15px", borderRadius: 12, cursor: "pointer",
                          border: sel ? `2px solid ${RED}` : "1.5px solid var(--border-c)", background: sel ? `color-mix(in srgb, ${RED} 10%, transparent)` : "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 600, fontSize: 14.5 }}>
                        <span style={{ width: 22, height: 22, borderRadius: lesson.qcm_multi ? 6 : "50%", border: `2px solid ${sel ? RED : "var(--border-c)"}`, background: sel ? RED : "transparent", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>{sel ? "✓" : ""}</span>
                        {o.text}
                      </button>
                    );
                  })}
                  {lesson.qcm_multi && <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>Plusieurs réponses possibles.</div>}
                  {result && (
                    <div data-testid="lecon-qcm-result" style={{ padding: "12px 15px", borderRadius: 12, fontWeight: 700, fontSize: 14, background: result.is_correct ? "#e8f7ec" : "#fff3e0", color: result.is_correct ? "#1a7f37" : "#b26a00" }}>
                      {result.is_correct ? "Bravo, bonne réponse ! ✓" : `Tu as ${result.score}/${result.total} bonne(s) réponse(s). Retente si tu veux.`}
                    </div>
                  )}
                </div>
              ) : lesson.exercise_type === "file" ? (
                <div style={{ marginTop: 8 }} data-testid="lecon-file-exercise">
                  <input id="lecon-file-input" type="file" accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.md,image/*" style={{ display: "none" }} onChange={onExFile} />
                  <button style={c.uploadBtn} onClick={() => document.getElementById("lecon-file-input")?.click()} disabled={busy} data-testid="lecon-file-upload">
                    <IoCloudUploadOutline size={17} /> {exFile ? "Remplacer mon fichier" : "Déposer mon fichier"}
                  </button>
                  {exFile && (
                    <button style={{ ...c.uploadBtn, marginTop: 8 }} onClick={() => openCarnetFile(exFile.file_id)} data-testid="lecon-file-view">
                      <IoDocumentAttachOutline size={16} /> {exFile.filename}
                    </button>
                  )}
                </div>
              ) : (
                <textarea style={c.textarea} value={submission} onChange={(e) => setSubmission(e.target.value)}
                  placeholder="Écris ici ta réponse / ton exercice…" rows={5} data-testid="lecon-submission" />
              )}
            </div>
          )}

          <div style={c.actions}>
            <button style={{ ...c.validate, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={validate}>
              {lesson.completed ? "✓ Mettre à jour mon exercice" : "Valider l'étape & poster mon exercice"}
            </button>
            {nextLesson ? (
              <button style={c.next} onClick={() => nav(`/ecole/lecon/${nextLesson.id}`)}>Suivant →</button>
            ) : (
              <button style={c.next} onClick={() => lesson.gate && nav(`/ecole/porte/${lesson.gate.id}`)}>Retour à la porte</button>
            )}
          </div>
        </div>

        <aside style={{ ...c.right, ...(isMobile ? { position: "static", top: "auto" } : {}) }}>
          <div style={c.qHead}>💬 Poser une question</div>
          <div style={c.qForm}>
            <textarea style={c.qInput} value={qText} onChange={(e) => setQText(e.target.value)} rows={3} placeholder="Ta question sur cette leçon…" />
            <button style={c.qBtn} onClick={postQuestion}>Envoyer</button>
          </div>
          <div style={c.qList}>
            {questions.length === 0 ? (
              <p style={c.qEmpty}>Sois le premier à poser une question 🙌</p>
            ) : questions.map((q) => (
              <div key={q.id} style={c.qItem}>
                <div style={c.qName}>{q.name}</div>
                <div style={c.qText}>{q.text}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Sommaire — tiroir latéral */}
      {somOpen && (
        <div style={c.drawerOverlay} onClick={() => setSomOpen(false)} data-testid="lecon-sommaire-drawer">
          <div style={c.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={c.drawerHead}>
              <span style={c.drawerTitle}>Sommaire</span>
              <button style={c.drawerX} onClick={() => setSomOpen(false)} data-testid="lecon-sommaire-close"><IoClose size={20} /></button>
            </div>

            <button style={c.drawerHub} data-testid="lecon-goto-formation" onClick={() => formation && nav(`/ecole/formation/${formation.id}`)}>
              <IoHomeOutline size={16} /> {formation?.title || "Hub de la formation"}
            </button>

            {formation?.gates?.length > 0 && (
              <div style={c.drawerSection}>
                <div style={c.drawerLabel}>Changer de Porte</div>
                <div style={c.porteWrap}>
                  {formation.gates.map((g) => (
                    <button key={g.id} data-testid={`lecon-porte-${g.id}`}
                      style={{ ...c.porteChip, ...(g.id === lesson.gate?.id ? c.porteChipCur : {}) }}
                      onClick={() => nav(`/ecole/porte/${g.id}`)}>
                      <span style={{ fontFamily: "var(--font-display)", marginRight: 6 }}>{g.kanji}</span>{g.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {gate && (
              <div style={c.drawerSection}>
                <div style={c.drawerLabel}>{gate.kanji} {gate.title}</div>
                <div style={c.drawerList}>
                  {(gate.modules || []).map((m) => (
                    <div key={m.id} style={{ marginBottom: 12 }}>
                      <div style={c.drawerModule}>{m.title}</div>
                      {(m.lessons || []).map((ls) => {
                        const cur = ls.id === lid;
                        return (
                          <button key={ls.id} data-testid={`lecon-nav-${ls.id}`} onClick={() => { setSomOpen(false); nav(`/ecole/lecon/${ls.id}`); }}
                            style={{ ...c.drawerLesson, ...(cur ? c.drawerLessonCur : {}) }}>
                            <span style={{ width: 18, textAlign: "center" }}>{ls.completed ? "✓" : "•"}</span>
                            <span style={{ flex: 1 }}>{ls.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </DojoShell>
  );
}

const c = {
  topBar: { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  somBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  uploadBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 10, border: "1px dashed var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", zIndex: 5000, display: "flex" },
  drawer: { width: "min(360px, 88vw)", height: "100%", overflowY: "auto", background: "var(--bg-secondary)", borderRight: "1px solid var(--border-c)", padding: "20px 18px", boxShadow: "8px 0 40px rgba(0,0,0,0.3)", animation: "slideInLeft 0.25s ease" },
  drawerHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  drawerTitle: { fontWeight: 900, fontSize: 20, color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  drawerX: { border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer" },
  drawerHub: { display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 14, cursor: "pointer", marginBottom: 18 },
  drawerSection: { marginBottom: 20 },
  drawerLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 1, color: "var(--text-secondary)", textTransform: "uppercase", marginBottom: 10 },
  porteWrap: { display: "flex", flexWrap: "wrap", gap: 7 },
  porteChip: { padding: "8px 12px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 12.5, cursor: "pointer" },
  porteChipCur: { background: RED, color: "#fff", borderColor: RED },
  drawerList: {},
  drawerModule: { fontWeight: 800, fontSize: 13.5, color: "var(--text-primary)", margin: "6px 0" },
  drawerLesson: { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "9px 10px", borderRadius: 9, border: "none", background: "transparent", color: "var(--text-secondary)", fontSize: 13.5, cursor: "pointer" },
  drawerLessonCur: { background: `color-mix(in srgb, ${RED} 12%, transparent)`, color: "var(--text-primary)", fontWeight: 700 },
  grid: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 24, alignItems: "start" },
  left: { minWidth: 0 },
  summary: { padding: "14px 16px", borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", marginBottom: 18 },
  summaryHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 },
  summaryTitle: { fontWeight: 800, fontSize: 14, color: "var(--text-primary)" },
  summaryProg: { fontWeight: 800, fontSize: 13, color: RED },
  summaryBar: { height: 6, background: "var(--bg-primary)", borderRadius: 999, overflow: "hidden", marginBottom: 12 },
  summaryFill: { height: "100%", background: RED },
  summaryList: { display: "flex", flexDirection: "column", gap: 8 },
  summaryModule: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  summaryModuleTitle: { fontSize: 12.5, color: "var(--text-secondary)", fontWeight: 600, minWidth: 120 },
  summaryPills: { display: "flex", gap: 6, flexWrap: "wrap" },
  pill: { width: 30, height: 30, borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-secondary)", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  pillDone: { background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: RED, borderColor: `color-mix(in srgb, var(--brand) 27%, transparent)` },
  pillCur: { background: RED, color: "#fff", borderColor: RED },
  h1: { fontSize: 26, fontWeight: 900, margin: "0 0 16px" },
  player: { position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 16, overflow: "hidden", background: "var(--text-primary)", marginBottom: 20 },
  iframe: { position: "absolute", inset: 0, width: "100%", height: "100%" },
  locked: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "#fff", textAlign: "center", padding: 20 },
  lockKanji: { fontSize: 40 },
  lockTxt: { fontWeight: 700, margin: 0 },
  lockSub: { fontSize: 13, color: "var(--text-secondary)", margin: 0 },
  card: { padding: "18px 20px", borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", marginBottom: 18, boxShadow: "var(--surface-shadow)" },
  cardTitle: { fontWeight: 800, fontSize: 15, marginBottom: 8, color: "var(--text-primary)" },
  resGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginTop: 4 },
  resCard: { display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-start", padding: "12px 14px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer", textAlign: "left" },
  resType: { fontSize: 10.5, fontWeight: 800, padding: "2px 9px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.5, background: "color-mix(in srgb, var(--brand) 14%, transparent)", color: RED },
  resTitle: { fontWeight: 700, fontSize: 14, color: "var(--text-primary)", lineHeight: 1.3 },
  resGo: { fontWeight: 800, fontSize: 12, color: RED, marginTop: 2 },
  rich: { fontSize: 14.5, color: "var(--text-secondary)", lineHeight: 1.7 },
  textarea: { width: "100%", marginTop: 12, padding: "12px 14px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, resize: "vertical", fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" },
  actions: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 },
  validate: { flex: 1, minWidth: 220, padding: "15px 20px", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: `0 8px 22px color-mix(in srgb, var(--brand) 27%, transparent)` },
  next: { padding: "15px 24px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--text-primary)", color: "var(--bg-primary)", fontWeight: 700, fontSize: 15, cursor: "pointer" },
  right: { position: "sticky", top: 84, padding: 20, borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  qHead: { fontWeight: 800, fontSize: 15, marginBottom: 12, color: "var(--text-primary)" },
  qForm: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 },
  qInput: { padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 13.5, resize: "vertical", fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" },
  qBtn: { padding: "10px 0", borderRadius: 9, border: "none", background: RED, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13.5 },
  qList: { display: "flex", flexDirection: "column", gap: 12, maxHeight: 380, overflowY: "auto" },
  qEmpty: { color: "var(--text-secondary)", fontSize: 13 },
  qItem: { padding: "10px 12px", borderRadius: 10, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)" },
  qName: { fontWeight: 700, fontSize: 13, color: RED },
  qText: { fontSize: 13.5, color: "var(--text-secondary)", marginTop: 2 },
};


if (typeof document !== "undefined" && !document.getElementById("lecon-kf")) {
  const el = document.createElement("style");
  el.id = "lecon-kf";
  el.textContent = "@keyframes slideInLeft{from{transform:translateX(-100%)}to{transform:translateX(0)}}";
  document.head.appendChild(el);
}
